import { AssessmentSelectDto, DomainSecondarySelectDto, SurveyListSelectDto, SurveySelectDto } from '@@types/api.types';
import { DropdownMenuComponent } from '@components/core/dropdown-menu.component';
import { IconButton } from '@components/core/icon-button.component';
import { Score } from '@components/shared/score.component';
import { TextWithLabel } from '@components/shared/text-with-label.component';
import { Tile } from '@components/shared/tile.component';
import { useSurveyNavigation } from '@hooks/surveys.hook';
import { cn } from '@lib/utils';
import { formatAssessmentDate, getTrlTitle, isMilitaryTrlAssessment } from '@utils/assessment.util';
import { MRL_TRL_SETTINGS } from '@utils/constants';
import type { Bucket } from '@utils/trl-buckets.util';
import { ChevronDownIcon, EllipsisVertical, ShieldIcon, StarIcon } from 'lucide-react';
import { FC, type ReactNode, useState } from 'react';

type TrlScoreTileProps = {
  assessment: AssessmentSelectDto | null;
  bucket?: Bucket | null;
  canManageAssessments?: boolean;
  /**
   * Draft survey for this use/domain combination (if any)
   */
  draftSurvey?: SurveyListSelectDto | null;
  isLoading?: boolean;
  /**
   * Callback to start a new TRL assessment
   */
  onStartNew?: () => void;
  subdomains: DomainSecondarySelectDto[];
  survey: SurveySelectDto | null;
  technologyId: string;
};

const getTrlDescription = (score: number | undefined | null): { description: string; label: string } | null => {
  if (!score || score === 0) return null;

  const levelData = MRL_TRL_SETTINGS.trl.levels.find((level) => level.level === score);
  return levelData ? { description: levelData.description, label: levelData.label } : null;
};

const renderTrlTitle = (params: { featured: boolean; isMilitaryTrl: boolean; title: string }): ReactNode => {
  const { featured, isMilitaryTrl, title } = params;

  const titleClassName = isMilitaryTrl ? 'text-teal-700' : 'text-[#3D2EFF]';
  const starClassName = isMilitaryTrl ? 'fill-teal-600 text-teal-600' : 'fill-yellow-400 text-yellow-500';

  return (
    <span className={cn('inline-flex items-center gap-1 text-[16px] font-semibold', titleClassName)}>
      <span>{title}</span>
      {isMilitaryTrl && <ShieldIcon className="size-3.5 text-teal-600" />}
      {featured && <StarIcon className={cn('size-3.5', starClassName)} />}
    </span>
  );
};

export const TrlScoreTile: FC<TrlScoreTileProps> = (props) => {
  const {
    assessment,
    bucket,
    canManageAssessments = false,
    draftSurvey,
    isLoading,
    onStartNew,
    subdomains,
    survey,
    technologyId,
  } = props;
  const [isSubdomainsExpanded, setIsSubdomainsExpanded] = useState(false);
  const { navigateToSurvey } = useSurveyNavigation();

  const trlDescription = assessment ? getTrlDescription(assessment.score) : null;
  const questionsCount = survey?.questions?.length ?? 0;
  const hasSurveyLink = Boolean(assessment?.surveyId && technologyId);

  const isMilitaryTrl = assessment
    ? isMilitaryTrlAssessment({ subtype: assessment.subtype, type: assessment.type })
    : false;
  const scoreTitle = assessment ? getTrlTitle({ subtype: assessment.subtype, type: assessment.type }) : 'TRL';
  const scoreTitleNode = assessment
    ? renderTrlTitle({ featured: assessment.featured, isMilitaryTrl, title: scoreTitle })
    : 'TRL';

  const toggleSubdomains = () => {
    setIsSubdomainsExpanded((prev) => !prev);
  };

  const handleReviewAnswers = () => {
    if (assessment?.surveyId && technologyId) {
      navigateToSurvey(`/technology/${technologyId}/survey/${assessment.surveyId}`);
    }
  };

  const handleResumeDraft = () => {
    if (draftSurvey && technologyId) {
      navigateToSurvey(`/technology/${technologyId}/survey/${draftSurvey.id}?viewFirstUnansweredQuestion=true`);
    }
  };

  // Build menu items based on available actions
  const menuItems = [
    ...(hasSurveyLink ? [{ onClick: handleReviewAnswers, text: 'Review Answers' }] : []),
    ...(canManageAssessments && draftSurvey ? [{ onClick: handleResumeDraft, text: 'Resume Draft' }] : []),
    ...(canManageAssessments && onStartNew ? [{ onClick: onStartNew, text: 'Start New Assessment' }] : []),
  ];

  const showMenu = menuItems.length > 0;

  return (
    <Tile
      emptyState={{ title: 'No TRL assessment' }}
      hoverReveal
      isEmpty={!assessment}
      isLoading={isLoading}
    >
      {assessment && (
        <div className="flex flex-col gap-4">
          {/* Score + Actions */}
          <div className="flex items-start justify-between">
            <Score
              colorVariant={isMilitaryTrl ? 'military' : 'default'}
              score={assessment.score}
              size="large"
              subtitle="Score"
              title={scoreTitleNode}
              type="TRL"
            />

            {showMenu && (
              <DropdownMenuComponent
                items={menuItems}
                trigger={
                  <IconButton.HoverReveal
                    ariaLabel="Open menu"
                    icon={<EllipsisVertical />}
                    variant="secondary"
                  />
                }
              />
            )}
          </div>

          {/* TRL Description */}
          {trlDescription && (
            <div className="rounded-lg bg-gray-100 p-3">
              <div className="mb-1 text-sm font-medium text-gray-600">
                TRL {assessment.score} - {trlDescription.label}
                {bucket && ` (${bucket.label})`}
              </div>
              <div className="text-xs leading-[18px] text-gray-500">{trlDescription.description}</div>
            </div>
          )}

          {/* Assessment Details */}
          <div className="flex flex-col gap-3 rounded-lg bg-gray-100 p-3">
            <div className="grid grid-cols-2 gap-3">
              <TextWithLabel
                className="flex-col-reverse text-sm"
                label="Use"
                labelClassName="text-xs"
                title={assessment.use.name}
              />
              <TextWithLabel
                className="flex-col-reverse text-sm"
                label="Domain"
                labelClassName="text-xs"
                title={assessment.domain?.name ?? 'N/A'}
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <TextWithLabel
                className="flex-col-reverse text-sm"
                label="Assessment Date"
                labelClassName="text-xs"
                title={formatAssessmentDate(assessment)}
              />
              <TextWithLabel
                className="flex-col-reverse text-sm"
                label="Questions"
                labelClassName="text-xs"
                title={questionsCount > 0 ? String(questionsCount) : 'N/A'}
              />
            </div>

            {/* Subdomains Accordion */}
            {subdomains.length > 0 && (
              <>
                <div className="my-1 h-px border-t border-gray-300" />

                <button
                  className="flex cursor-pointer items-center justify-between"
                  onClick={toggleSubdomains}
                  type="button"
                >
                  <span className="text-xs font-normal text-gray-600">
                    {subdomains.length} Subdomain{subdomains.length === 1 ? '' : 's'}
                  </span>
                  <ChevronDownIcon
                    className={cn(
                      'text-gray-500 transition-transform duration-200',
                      isSubdomainsExpanded && 'rotate-180',
                    )}
                    size={20}
                  />
                </button>

                {isSubdomainsExpanded && (
                  <div className="mt-1 flex flex-col gap-0.5">
                    {subdomains.map((subdomain) => (
                      <div
                        className="flex flex-row items-center"
                        key={subdomain.id}
                      >
                        <span className="text-xs font-light text-gray-600">{subdomain.name}</span>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </Tile>
  );
};
